// ===========================================
// Sinistres — Page liste refondue (UX cohérent avec les autres pages)
// ===========================================
import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
    Table, Card, Input, Select, Button, Tag, Grid, DatePicker, Empty,
    List, Pagination, Spin, Dropdown, Tooltip, Modal,
} from 'antd';
import {
    SearchOutlined, PlusOutlined, EyeOutlined, WarningOutlined, EditOutlined,
    DeleteOutlined, MoreOutlined, ClearOutlined, FileProtectOutlined,
    DollarOutlined, CheckCircleOutlined, ClockCircleOutlined,
    QuestionCircleOutlined, CloseOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { sinistresService, settingsService } from '../../common/services';
import { useDebounce, useGlobalAgence } from '../../common/hooks';
import { SINISTRE_STATUTS, getSinistreStatutColor } from '../../common/constants';
import { formatMontant } from '../../common/utils/format';
import MobileFab from '../../components/mobileFab';
import { useHasPermission } from '../../components/can';

import MiniKpiCard from '../clients/components/MiniKpiCard';

import './sinistres.page.scss';

const { Option } = Select;
const { RangePicker } = DatePicker;

const FALLBACK_STATUTS = Object.values(SINISTRE_STATUTS).map((s) => ({ code: s.code, label: s.label }));

// Statuts considérés comme "ouverts" (à traiter)
const STATUS_OUVERTS = ['ouvert', 'en_cours', 'enquete', 'expertise'];
const STATUS_CLOS = ['clos', 'regle', 'rejete'];

const Sinistres = () => {
    const navigate = useNavigate();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;

    // ─── Permissions (masquer les actions non autorisées) ─────────────
    const canUpdate = useHasPermission('sinistres:update');
    const canDelete = useHasPermission('sinistres:delete');

    // ─── État ─────────────────────────────────────────────────────────
    const [sinistres, setSinistres] = useState([]);
    const [guideOpen, setGuideOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 400);
    const [statutsList, setStatutsList] = useState(FALLBACK_STATUTS);
    const [filterStatut, setFilterStatut] = useState('all');
    const [filterDates, setFilterDates] = useState(null);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const globalAgence = useGlobalAgence();

    const fetchSinistres = async (page = 1, limit = 20) => {
        setLoading(true);
        try {
            const params = {
                page, limit,
                q: debouncedSearch || undefined,
                statut: filterStatut !== 'all' ? filterStatut : undefined,
                agence: globalAgence !== 'all' ? globalAgence : undefined,
                start: filterDates?.[0]?.format('YYYY-MM-DD') || undefined,
                end: filterDates?.[1]?.format('YYYY-MM-DD') || undefined,
            };
            const res = await sinistresService.getAll(params);
            const data = res?.data || res;
            setSinistres(data?.items || []);
            setPagination((prev) => ({
                ...prev,
                current: data?.page || page,
                total: data?.total || 0,
                pageSize: limit,
            }));
        } catch (error) {
            toast.error('Erreur lors du chargement des sinistres');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSinistres(1, pagination.pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch, filterStatut, filterDates, globalAgence]);

    // Statuts dynamiques (settings)
    useEffect(() => {
        const loadStatuts = async () => {
            try {
                const res = await settingsService.getAll('sinistres');
                const raw = res?.data || res;
                const list = raw?.data || raw;
                if (Array.isArray(list)) {
                    const found = list.find((s) => s.key === 'STATUTS_SINISTRE');
                    if (found && Array.isArray(found.value) && found.value.length > 0) {
                        setStatutsList(found.value);
                    }
                }
            } catch (_) { /* */ }
        };
        loadStatuts();
    }, []);

    const getStatutLabel = (code) => {
        const found = statutsList.find((s) => s.code === code);
        return found ? found.label : code || '—';
    };

    // ─── KPIs calculés depuis la page courante ────────────────────────
    const stats = useMemo(() => {
        const total = sinistres.length;
        let enCours = 0;
        let clos = 0;
        let montantTotal = 0;
        sinistres.forEach((r) => {
            montantTotal += r.montantEstime || 0;
            if (STATUS_OUVERTS.includes(r.statut)) enCours++;
            else if (STATUS_CLOS.includes(r.statut)) clos++;
        });
        return { total, enCours, clos, montantTotal };
    }, [sinistres]);

    // ─── Filtres ──────────────────────────────────────────────────────
    const activeFiltersCount = useMemo(() => {
        let n = 0;
        if (filterDates) n++;
        return n;
    }, [filterDates]);

    const clearAllFilters = () => {
        setSearch('');
        setFilterStatut('all');
        setFilterDates(null);
    };

    // ─── Suppression optimiste ────────────────────────────────────────
    const handleDelete = async (id) => {
        const previous = sinistres;
        setSinistres((prev) => prev.filter((s) => s._id !== id));
        try {
            await sinistresService.delete(id);
            toast.success('Sinistre supprimé');
            setPagination((p) => ({ ...p, total: Math.max(0, p.total - 1) }));
        } catch (e) {
            setSinistres(previous);
            toast.error(e.message || 'Erreur lors de la suppression');
        }
    };

    // ─── Menu actions par ligne ───────────────────────────────────────
    const getRowActionsMenu = (record) => ({
        items: [
            canUpdate && {
                key: 'edit',
                icon: <EditOutlined />,
                label: 'Modifier',
            },
            canUpdate && canDelete && { type: 'divider' },
            canDelete && {
                key: 'delete',
                icon: <DeleteOutlined />,
                label: 'Supprimer',
                danger: true,
            },
        ].filter(Boolean),
        onClick: ({ key, domEvent }) => {
            domEvent?.stopPropagation?.();
            if (key === 'edit') {
                navigate(`/dashboard/nouveau-sinistre?edit=${record._id}`);
            } else if (key === 'delete') {
                if (window.confirm(`Supprimer le sinistre n° ${record.contrat?.numeroPolice || ''} ?`)) {
                    handleDelete(record._id);
                }
            }
        },
    });

    // ─── Colonnes ─────────────────────────────────────────────────────
    const columns = [
        {
            title: 'N° Police',
            dataIndex: ['contrat', 'numeroPolice'],
            key: 'police',
            width: 150,
            render: (t, r) => (
                <button
                    type="button"
                    className="sinistre-cell-police"
                    onClick={() => navigate(`/dashboard/contrat/${r.contrat?._id || r.contrat}`)}
                >
                    {t || '—'}
                </button>
            ),
        },
        {
            title: 'Client',
            key: 'client',
            render: (_, r) => {
                if (!r.client) return '—';
                const name = r.client.raisonSociale
                    || `${r.client.nom || ''} ${r.client.prenom || ''}`.trim()
                    || '—';
                return <span className="sinistre-cell-name">{name}</span>;
            },
            ellipsis: true,
        },
        {
            title: 'Date sinistre',
            dataIndex: 'dateSinistre',
            key: 'date',
            width: 130,
            sorter: (a, b) => new Date(a.dateSinistre || 0) - new Date(b.dateSinistre || 0),
            render: (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—',
        },
        {
            title: 'Type',
            dataIndex: 'typeSinistre',
            key: 'type',
            width: 150,
            render: (t) => t ? <Tag color="orange">{t}</Tag> : '—',
            responsive: ['md'],
        },
        {
            title: 'Statut',
            dataIndex: 'statut',
            key: 'statut',
            width: 130,
            filters: statutsList.map((s) => ({ text: s.label, value: s.code })),
            filterMultiple: false,
            filteredValue: filterStatut !== 'all' ? [filterStatut] : null,
            render: (s) => (
                <Tag color={getSinistreStatutColor(s)}>{getStatutLabel(s)}</Tag>
            ),
        },
        {
            title: 'Montant estimé',
            dataIndex: 'montantEstime',
            key: 'montant',
            width: 150,
            align: 'right',
            sorter: (a, b) => (a.montantEstime || 0) - (b.montantEstime || 0),
            render: (v) => v ? <span className="sinistre-cell-amount">{formatMontant(v)}</span> : '—',
            responsive: ['lg'],
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 160,
            align: 'center',
            fixed: 'right',
            render: (_, r) => (
                <div className="sinistre-cell-actions">
                    <Button
                        type="primary"
                        size="small"
                        icon={<EyeOutlined />}
                        className="btn-detail sinistre-action-detail"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/dashboard/sinistre/${r._id}`);
                        }}
                    >
                        Détail
                    </Button>
                    {(canUpdate || canDelete) && (
                        <Dropdown menu={getRowActionsMenu(r)} trigger={['click']} placement="bottomRight">
                            <Button
                                type="text"
                                size="small"
                                icon={<MoreOutlined />}
                                onClick={(e) => e.stopPropagation()}
                                aria-label="Plus d'actions"
                                className="sinistre-action-more"
                            />
                        </Dropdown>
                    )}
                </div>
            ),
        },
    ];

    // ─── Handler du filtre Statut (depuis le column header) ───────────
    const handleTableChange = (_pag, filters) => {
        const next = filters?.statut?.[0];
        setFilterStatut(next || 'all');
    };

    return (
        <div className="sinistres-page">
            {/* Header */}
            <header className="sinistres-header">
                <div className="sinistres-header-text">
                    <h1 className="sinistres-title">
                        <WarningOutlined /> Sinistres
                    </h1>
                    <p className="sinistres-subtitle">Déclarations et suivi des sinistres</p>
                </div>
                <div className="sinistres-header-actions">
                    <Button
                        icon={<QuestionCircleOutlined />}
                        onClick={() => setGuideOpen(true)}
                        style={{ borderRadius: 8, background: '#1A2B5F', borderColor: '#1A2B5F', color: '#fff', fontWeight: 600 }}
                    >
                        {!isMobile && 'Guide'}
                    </Button>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => navigate('/dashboard/nouveau-sinistre')}
                        className="sinistres-btn-new"
                    >
                        {!isMobile && 'Nouvelle déclaration'}
                    </Button>
                </div>
            </header>

            <Modal
                open={guideOpen}
                onCancel={() => setGuideOpen(false)}
                title={<span><QuestionCircleOutlined style={{ color: '#1A2B5F', marginRight: 8 }} />Guide — Gérer les sinistres</span>}
                closeIcon={(
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px',
                        background: '#C8102E', border: '1px solid #C8102E', borderRadius: 8,
                        fontSize: '0.9rem', fontWeight: 600, color: '#fff',
                    }}>
                        <CloseOutlined /> Fermer
                    </span>
                )}
                footer={<Button type="primary" onClick={() => setGuideOpen(false)}>J'ai compris</Button>}
                width={620}
            >
                <span style={{ color: '#8c8c8c' }}>
                    Cette page liste les déclarations de sinistres et permet d'en suivre le
                    traitement.
                </span>
                {(() => {
                    const liStyle = {
                        display: 'flex', gap: 12, padding: '14px 16px', marginBottom: 10,
                        background: '#f6f8fb', border: '1px solid #E8ECF1', borderRadius: 12, lineHeight: 1.5,
                    };
                    const icStyle = { flexShrink: 0, fontSize: 18, color: '#1A2B5F', marginTop: 2 };
                    return (
                        <div style={{ marginTop: 16 }}>
                            <div style={liStyle}>
                                <WarningOutlined style={icStyle} />
                                <div>
                                    <strong>1 · Indicateurs</strong> — Nombre de sinistres déclarés,
                                    en cours et clôturés, recalculés selon les filtres.
                                </div>
                            </div>
                            <div style={liStyle}>
                                <SearchOutlined style={icStyle} />
                                <div>
                                    <strong>2 · Rechercher &amp; filtrer</strong> — Recherchez par
                                    client ou n° de police, puis filtrez par <strong>statut</strong>
                                    et <strong>période</strong>.
                                </div>
                            </div>
                            <div style={liStyle}>
                                <ClockCircleOutlined style={icStyle} />
                                <div>
                                    <strong>3 · Suivre le statut</strong> — Un sinistre évolue de
                                    « déclaré » à « en cours d'instruction » puis « clôturé /
                                    indemnisé ». Le badge de statut indique où en est le dossier.
                                </div>
                            </div>
                            <div style={liStyle}>
                                <EyeOutlined style={icStyle} />
                                <div>
                                    <strong>4 · Actions par ligne</strong> — « Détail » ouvre le
                                    dossier complet ; le menu <strong>⋮</strong> permet de
                                    <strong> modifier</strong> ou <strong>supprimer</strong> la
                                    déclaration.
                                </div>
                            </div>
                            <div style={{ ...liStyle, marginBottom: 0 }}>
                                <PlusOutlined style={icStyle} />
                                <div>
                                    <strong>5 · Nouvelle déclaration</strong> — Le bouton « Nouvelle
                                    déclaration » enregistre un sinistre (un contrat existant doit
                                    être sélectionné).
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </Modal>

            {/* KPIs */}
            <section className="sinistres-kpis" aria-label="Indicateurs sinistres">
                <MiniKpiCard
                    icon={<FileProtectOutlined />}
                    label="Total sinistres"
                    value={stats.total}
                    tone="blue"
                />
                <MiniKpiCard
                    icon={<ClockCircleOutlined />}
                    label="En cours"
                    value={stats.enCours}
                    tone="orange"
                />
                <MiniKpiCard
                    icon={<CheckCircleOutlined />}
                    label="Clos / Réglés"
                    value={stats.clos}
                    tone="green"
                />
                <MiniKpiCard
                    icon={<DollarOutlined />}
                    label="Montant estimé"
                    value={formatMontant(stats.montantTotal)}
                    tone="red"
                />
            </section>

            {/* Carte principale */}
            <Card className="sinistres-main-card">
                <div className="sinistres-toolbar">
                    <Input
                        allowClear
                        placeholder="Rechercher (n° police, client...)"
                        prefix={<SearchOutlined style={{ color: 'var(--cap-text-muted)' }} />}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="sinistres-search"
                        aria-label="Rechercher un sinistre"
                    />
                    <RangePicker
                        format="DD/MM/YYYY"
                        placeholder={['Date début', 'Date fin']}
                        onChange={setFilterDates}
                        value={filterDates}
                        className="sinistres-filter-dates"
                        allowClear
                    />
                    {(activeFiltersCount > 0 || search || filterStatut !== 'all') && (
                        <Button
                            icon={<ClearOutlined />}
                            onClick={clearAllFilters}
                            className="sinistres-btn-clear"
                        >
                            Effacer
                        </Button>
                    )}
                </div>

                {isMobile ? (
                    <Spin spinning={loading}>
                        {sinistres.length === 0 ? (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="Aucun sinistre déclaré"
                                style={{ padding: 32 }}
                            >
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => navigate('/dashboard/nouveau-sinistre')}
                                    className="sinistres-btn-new"
                                >
                                    Déclarer un sinistre
                                </Button>
                            </Empty>
                        ) : (
                            <List
                                dataSource={sinistres}
                                rowKey="_id"
                                split={false}
                                renderItem={(r) => {
                                    const name = r.client?.raisonSociale
                                        || `${r.client?.nom || ''} ${r.client?.prenom || ''}`.trim()
                                        || '—';
                                    return (
                                        <Card
                                            key={r._id}
                                            size="small"
                                            className="sinistres-mobile-card"
                                            onClick={() => navigate(`/dashboard/sinistre/${r._id}`)}
                                        >
                                            <div className="mobile-card-head">
                                                <span className="mobile-card-police">{r.contrat?.numeroPolice || '—'}</span>
                                                <Tag color={getSinistreStatutColor(r.statut)}>
                                                    {getStatutLabel(r.statut)}
                                                </Tag>
                                            </div>
                                            <div className="mobile-card-name">{name}</div>
                                            <div className="mobile-card-meta">
                                                {r.typeSinistre && <Tag color="orange">{r.typeSinistre}</Tag>}
                                                <span>{r.dateSinistre ? new Date(r.dateSinistre).toLocaleDateString('fr-FR') : '—'}</span>
                                            </div>
                                            {r.montantEstime > 0 && (
                                                <div className="mobile-card-footer">
                                                    <span className="sinistre-cell-amount">{formatMontant(r.montantEstime)}</span>
                                                    <Dropdown menu={getRowActionsMenu(r)} trigger={['click']}>
                                                        <Button
                                                            type="text"
                                                            icon={<MoreOutlined />}
                                                            onClick={(e) => e.stopPropagation()}
                                                            aria-label="Actions"
                                                        />
                                                    </Dropdown>
                                                </div>
                                            )}
                                        </Card>
                                    );
                                }}
                            />
                        )}
                        <div className="sinistres-pagination-wrapper">
                            <Pagination
                                current={pagination.current}
                                pageSize={pagination.pageSize}
                                total={pagination.total}
                                onChange={(p, ps) => fetchSinistres(p, ps)}
                                size="small"
                                simple
                            />
                        </div>
                    </Spin>
                ) : (
                    <Table
                        dataSource={sinistres}
                        columns={columns}
                        rowKey="_id"
                        loading={{ spinning: loading, size: 'large', tip: 'Chargement…' }}
                        onChange={handleTableChange}
                        pagination={{
                            ...pagination,
                            onChange: (p, ps) => fetchSinistres(p, ps),
                            showSizeChanger: true,
                            showTotal: (t, range) => `${range[0]}-${range[1]} sur ${t} sinistre${t > 1 ? 's' : ''}`,
                        }}
                        size="middle"
                        scroll={{ x: 'max-content' }}
                        onRow={(record) => ({
                            onClick: () => navigate(`/dashboard/sinistre/${record._id}`),
                            style: { cursor: 'pointer' },
                        })}
                        locale={{
                            emptyText: loading ? (
                                <div style={{ minHeight: 220 }} />
                            ) : (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={
                                        activeFiltersCount > 0 || search || filterStatut !== 'all'
                                            ? 'Aucun sinistre ne correspond aux filtres'
                                            : 'Aucun sinistre déclaré'
                                    }
                                >
                                    {(activeFiltersCount > 0 || search || filterStatut !== 'all') ? (
                                        <Button icon={<ClearOutlined />} onClick={clearAllFilters}>
                                            Effacer les filtres
                                        </Button>
                                    ) : (
                                        <Button
                                            type="primary"
                                            icon={<PlusOutlined />}
                                            onClick={() => navigate('/dashboard/nouveau-sinistre')}
                                            className="sinistres-btn-new"
                                        >
                                            Déclarer un sinistre
                                        </Button>
                                    )}
                                </Empty>
                            ),
                        }}
                    />
                )}
            </Card>

            <MobileFab
                icon={<WarningOutlined />}
                onClick={() => navigate('/dashboard/nouveau-sinistre')}
                title="Déclarer un sinistre"
                color="#fa8c16"
            />
        </div>
    );
};

export default Sinistres;
